import React, { useCallback } from 'react';

import classnames from 'classnames';

import { useMediaQuery } from '@mui/material';

import HorizontalScroller from 'components/uiLibrary/HorizontalScroller';
import LinkButton from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import AppliedFilters from 'components/Filters/Display/AppliedFilters';
import useTracking from 'components/Globals/Analytics';
import { COMPONENTS, GOOGLE_OLD_TRACKING_SERVICES, SECTIONS } from 'components/Globals/Analytics/constants';
import ProViewStickyHeader from 'components/Globals/Layout/ProViewStickyHeader';

import { TP } from 'constants/index';

import useAppContext from 'utils/hooks/useAppContext';
import useOnScrollBeyondElement from 'utils/hooks/useOnScrollBeyondElement';
import usePageContext from 'utils/hooks/usePageContext';
import { ARTIST_PRO_VIEW, TRACK_EVENTS } from 'utils/tracking';

import { useTranslation } from 'src/i18n';

import classes from './ContentWithFilters.module.scss';

const viewInPublicSiteTrackingData = {
  section: SECTIONS.ENTITY_HEADER,
  component: COMPONENTS.VIEW_IN_PUBLIC_SITE_CTA,
};

const PublicViewCTA = ({ onClickHandler }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');

  return (
    <LinkButton
      variant="text"
      size="medium"
      styles={{
        root: classes.publicViewCTA,
      }}
      rightIcon={<SpriteIcon icon="arrow_right_link" size={12} />}
      onClick={onClickHandler}
      trackingData={viewInPublicSiteTrackingData}
    >
      <Typography size="12" italic>
        {t(`${TP}.VIEW_IN`)}
        <Typography className={classes.publicViewCTA__site} weight="medium" size="12" color="orange">
          {t(`${TP}.FN_PUBLIC_SITE`)}
        </Typography>
      </Typography>
    </LinkButton>
  );
};

const Tabs = ({ tabs, activeTabKey, ns, trackingData, showDivider = true, onClickTab }) => {
  const { t } = useTranslation(ns);
  const { navigate } = usePageContext();
  const { obRouteContext } = useAppContext();
  const track = useTracking();

  const onClickHandler = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      navigate.to(
        navigate.getLinkProps({
          ...obRouteContext?.linkProps,
          pro: false,
          basePath: null,
          tab: null,
        }),
      );

      track.click(
        {
          name: TRACK_EVENTS.CLICK,
          data: {
            id: ARTIST_PRO_VIEW.ARTIST_PRO_VIEW_SWITCH,
            tab: 'public',
          },
        },
        GOOGLE_OLD_TRACKING_SERVICES,
      );
    },
    [navigate, obRouteContext?.linkProps, track],
  );
  return (
    tabs?.length > 0 && (
      <div>
        <div className={classes.tabHeader}>
          <nav className={classes.navigation}>
            <HorizontalScroller hideArrow>
              <div className={classes.navigation__tabs}>
                {tabs.map((tab, index) => (
                  <LinkButton
                    variant="grey"
                    shape="rectangle"
                    active={activeTabKey === tab.key}
                    styles={{
                      root: classes.navigation__tabs_tab,
                    }}
                    {...tab.linkProps}
                    key={`mlink_${index}`}
                    onClick={e => {
                      navigate.scrollTo({ behavior: 'auto' });

                      if (onClickTab) {
                        onClickTab(e);
                      }
                    }}
                    stopPropagation
                    disableUnderline
                    disableHover
                    isLink
                    trackingData={{
                      ...trackingData,
                      meta: {
                        tab: tab?.key,
                      },
                    }}
                  >
                    {activeTabKey === tab.key ? (
                      <Typography color="white" weight="bold" size="12">
                        {t(tab.title)}
                      </Typography>
                    ) : (
                      t(tab.title)
                    )}
                  </LinkButton>
                ))}
              </div>
            </HorizontalScroller>
          </nav>
          <PublicViewCTA onClickHandler={onClickHandler} />
        </div>
        {showDivider && <div className={classes.tabHeader__divider} />}
      </div>
    )
  );
};

const ContentWithFilters = ({
  ns,
  filterTypes,
  tabs,
  activeTabKey,
  entity,
  entityType,
  children,
  footer,
  showStickyHeader = false,
  hideAppliedFilters = false,
  tabsListTrackingData,
  styles = { content: '' },
  header,
  showDivider = true,
  onClickTab,
}) => {
  const isMobile = useMediaQuery('(max-width: 800px)');
  const { ref, isScrolledBeyond } = useOnScrollBeyondElement();

  return (
    <>
      {header && <div className={classes.header}>{header}</div>}
      {showStickyHeader && <ProViewStickyHeader isScrolledBeyond={isScrolledBeyond} />}
      {/** NOTE: This div is used to trigger the sticky header */}
      <div ref={ref} />
      <div
        className={classnames(classes.root, {
          [classes.withHeader]: header,
        })}
      >
        {isMobile && (
          <Tabs
            ns={ns}
            tabs={tabs}
            activeTabKey={activeTabKey}
            entity={entity}
            entityType={entityType}
            trackingData={tabsListTrackingData}
            showDivider={showDivider}
            onClickTab={onClickTab}
          />
        )}
        <div className={classnames(classes.root__content, { [styles.content]: !!styles.content })}>
          {!isMobile && (
            <div>
              <Tabs
                ns={ns}
                tabs={tabs}
                activeTabKey={activeTabKey}
                entity={entity}
                entityType={entityType}
                trackingData={tabsListTrackingData}
                showDivider={showDivider}
                onClickTab={onClickTab}
              />
            </div>
          )}

          <div className={classes.tab}>
            {!hideAppliedFilters && filterTypes?.length > 0 && <AppliedFilters className={classes.tabFilters} />}
            {children}
          </div>
        </div>
      </div>
      {footer && <div className={classes.footer}>{footer}</div>}
    </>
  );
};

export default ContentWithFilters;
